/**
 * analytics plugin
 *
 * Emits the `gallery_interaction` behavioral event
 * (library/analytics/TRACKING_PLAN.md §3.4)
 * for genuine user interaction with any `.swiper-section` carousel:
 * a swipe/drag (`touchEnd`), a nav-arrow click, or a pagination-bullet
 * click, followed by the resulting `slideChange`.
 *
 * Mirrors the autoplay plugin's interaction-detection pattern — hover and
 * programmatic movement (autoplay ticks, synced-swiper pushes, our own
 * slideTo calls) never emit events, because none of them trip the
 * interaction listeners.
 *
 * Throttling: at most one event per slide index per swiper per page view,
 * tracked in a WeakMap so destroyed swipers leave no residue. The event is
 * pushed through smTrack (consent gate + envelope) with the section's
 * DOM id as `section_id`.
 */

// window.smTrack is registered by assets/sm-track.js (its own script tag,
// loaded before this bundle — see that file's LOADING MODEL note for why
// this is a global instead of an import). No-op-safe if it's ever missing.
// Named uniquely (not `smTrack`) because classic-script bundles share the
// page's global lexical scope — see the same note in sm-analytics.js.
const smTrackSwiper = (eventName, params) => {
  if (typeof window !== 'undefined' && typeof window.smTrack === 'function') {
    window.smTrack(eventName, params);
  }
};

const stateBySwiper = new WeakMap();

function sectionIdOf(section) {
  const wrapper = section.closest('[id^="shopify-section-"]');
  if (wrapper) return wrapper.id.replace('shopify-section-', '');
  return section.id || section.className.split(' ')[0] || 'swiper-section';
}

export const analytics = {
  name: 'analytics',

  afterInit(swiper, ctx) {
    const section = ctx.parentSwiperSection || ctx.section;
    if (!section) return;

    const state = { interacted: false, seenIndexes: new Set(), cleanups: [] };
    stateBySwiper.set(swiper, state);

    const markInteracted = () => {
      state.interacted = true;
    };

    // Swipe / drag (touchEnd fires for touch and simulated mouse drag).
    swiper.on('touchEnd', markInteracted);
    state.cleanups.push(() => {
      try {
        swiper.off('touchEnd', markInteracted);
      } catch {
        // swiper already destroyed
      }
    });

    // Nav arrows + pagination (same selectors the autoplay plugin uses).
    [
      section.querySelector('[class*="__button--prev"]'),
      section.querySelector('[class*="__button--next"]'),
      section.querySelector('[class*="__pagination"]'),
    ].forEach((el) => {
      if (!el) return;
      el.addEventListener('click', markInteracted);
      state.cleanups.push(() => el.removeEventListener('click', markInteracted));
    });

    const onSlideChange = () => {
      if (!state.interacted) return;
      const index = swiper.activeIndex;
      if (state.seenIndexes.has(index)) return;
      state.seenIndexes.add(index);
      smTrackSwiper('gallery_interaction', {
        interaction_type: 'swipe',
        media_index: index,
        media_type: 'image',
        section_id: sectionIdOf(section),
      });
    };
    swiper.on('slideChange', onSlideChange);
    state.cleanups.push(() => {
      try {
        swiper.off('slideChange', onSlideChange);
      } catch {
        // swiper already destroyed
      }
    });
  },

  onDestroy(swiper) {
    const state = stateBySwiper.get(swiper);
    if (!state) return;
    for (const cleanup of state.cleanups) {
      try {
        cleanup();
      } catch (err) {
        console.warn('analytics: listener cleanup failed', err);
      }
    }
    stateBySwiper.delete(swiper);
  },
};

export default analytics;
